import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit} from '@angular/core';
import {SaleTaskService} from '@service/crm/sale-task.service';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {AssignToModalComponent} from '../../../components/v2/assign-to-modal/assign-to-modal.component';
import {AuthService} from '@service/auth/auth.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {CustomerDetailCrmComponent} from "../customer-detail-crm/customer-detail-crm.component";

@Component({
    selector: 'app-task-detail',
    templateUrl: './task-detail.component.html',
    styleUrls: ['./task-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TaskDetailComponent implements OnInit, OnDestroy {
  @Input() id!: number;
  @Input() customerClickable!: boolean;
  private _task: any = {};
  public noteForm: UntypedFormGroup;
  loaded = false;
  modified = false;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private dialog: MatDialog,
    private cd: ChangeDetectorRef,
    private authService: AuthService,
    private dialogRef: MatDialogRef<TaskDetailComponent>,
    private saleTasksService: SaleTaskService,
  ) {
    this.noteForm = this.fb.group({
      note: [null, Validators.required]
    });
  }

  ngOnInit(): void {
    this.saleTasksService.getTaskDetail(this.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((x) => this.task = x);
  }

  ngOnDestroy(): void {
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }

  get task(): { editable: boolean, [key: string]: any } {
    return this._task;
  }

  set task(value: { editable: boolean, [key: string]: any }) {
    this._task = value;
    this.noteForm.patchValue(value);
    if (this.task.editable) {
      this.noteForm.enable();
    } else {
      this.noteForm.disable();
    }
    this.loaded = true;
    this.cd.detectChanges();
  }

  closeDialog(): void {
    this.dialog.getDialogById('taskDetail')?.close({modified: this.modified});
  }

  completeTask(): void {
    this.loaded = false;
    this.saleTasksService.setTaskCompleted(this.task.id, this.noteForm.getRawValue())
      .pipe(takeUntil(this.destroy$))
      .subscribe((x) => {
        this.task = x;
        this.modified = true;
        this.closeDialog();
      });
  }

  openAssignToModal(): void {
    const assignToModal = this.dialog.open(AssignToModalComponent, {
      width: '570px',
      height: '650px',
      panelClass: 'p-0'
    });
    assignToModal.componentInstance.customerId = this.task?.customerId;
    assignToModal.componentInstance.modified
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        assignToModal.close();
        this.modified = true;
        this.closeDialog();
      });
    assignToModal.componentInstance.closeButton
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        assignToModal.close();
      });
  }

  openCustomerWithId(id: number): void {
    const template = this.dialog.open(CustomerDetailCrmComponent, {
      width: '90%',
      height: '90%',
      panelClass: 'p-0',
      disableClose: true
    });
    template.componentInstance.customerId = id;
    template.componentInstance.showTaskTab = false;
  }

  saveTask(): void {
    this.loaded = false;
    this.saleTasksService.saveTask(this.task.id, this.noteForm.getRawValue())
      .pipe(takeUntil(this.destroy$))
      .subscribe((x) => this.task = x);
  }

  canAssignTo(): boolean {
    return this.authService.currentUserPermission.canAssignCrmTaskToOthers;
  }
}
